import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import EVMapViewer from '@eagleview/mapviewer-react';
import {
  EVBox,
  EVToolbarPanel,
  EVIconButton,
  EVAdd,
  EVRemove,
  useTheme,
} from '@eagleview/ev-comp-library';
import { MAPBOX_KEY } from 'constants.js';
import Compass from 'assets/compassWireframe.svg';
// Import the same anomaly image assets as Adjuster.js
import selectedImg from 'assets/anomaly-hovered.svg';
import hiddenImg from 'assets/hidden.svg';
import userAnomalyImg from 'assets/manual-count.svg';
import userAnomalySelectedImg from 'assets/manual-selected.svg';
import opportunityImg from 'assets/opportunity.svg';
import validatedImg from 'assets/validated.svg';
import {
  TEST_SQUARE_WIREFRAME_MIN_ZOOM_LEVEL,
  TEST_SQUARE_WIREFRAME_MAX_ZOOM_LEVEL,
  TESTSQUARE_ZOOM_LEVEL_ON_SELECTION,
} from 'layout/adjuster/Adjuster.constants';
import { useSelector } from 'react-redux';
import { get, isEmpty } from 'lodash';
import * as util from 'layout/adjuster/Adjuster.utils';
import PropTypes from 'prop-types';
import useStyles from './TestSquareWireFrameMapViewer.styles';

const TestSquareWireFrameMapViewer = forwardRef(({
  facetPolygons, anomalies, onAnomalyHover, hoveredAnomalyId,
}, ref) => {
  // derived values
  const MAPVIEWER_MAX_ZOOM = TEST_SQUARE_WIREFRAME_MAX_ZOOM_LEVEL;
  const MAPVIEWER_MIN_ZOOM = TEST_SQUARE_WIREFRAME_MIN_ZOOM_LEVEL;

  const styles = useStyles();
  const theme = useTheme();
  const wireframeMapviewerRef = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [bounds, setBounds] = useState();
  const [interactionObject, setInteractionObject] = useState({});
  const testSquare = useSelector((state) => state.adjusterReducer.testSquareData);

  // Add ref to track if component is mounted
  const isMountedRef = useRef(true);

  const [currentView, setCurrentView] = useState({
    zoom: MAPVIEWER_MIN_ZOOM,
    pitch: 0,
    rotation: 0,
    lonLat: {
      lon: 0,
      lat: 0,
    },
  });
  const [localCurrentView, setLocalCurrentView] = useState(currentView);
  const [initialZoomLevel, setInitialZoomLevel] = useState();
  const [hasZoomedToTestSquare, setHasZoomedToTestSquare] = useState(false);

  // Asset states - following PhotosMapViewer pattern
  const [anomalyAsset, setAnomalyAsset] = useState(null);
  const anomalyAssetRef = useRef(null);

  useEffect(() => {
    if (!wireframeMapviewerRef.current || !facetPolygons || !facetPolygons.data || !isMapReady) return;
    const { mapContainer } = wireframeMapviewerRef.current;
    const { clientWidth, clientHeight } = mapContainer;

    // Extract all coordinates from all facet polygons
    const allCoordinates = facetPolygons.data.flatMap((facet) => get(facet, 'geometries[0].coordinates[0]', []));

    // Add test square coordinates if available
    if (testSquare && testSquare.geometry && testSquare.geometry.coordinates) {
      // Test square geometry is typically a Polygon, so we need the outer ring coordinates
      const testSquareCoordinates = get(testSquare, 'geometry.coordinates[0]', []);
      if (testSquareCoordinates.length > 0) {
        allCoordinates.push(...testSquareCoordinates);
      }
    }

    if (allCoordinates.length > 0) {
      const b = util.getPerfectBounds(allCoordinates, clientWidth, clientHeight);
      if (isMountedRef.current) {
        setBounds(b);
      }
    }
  }, [facetPolygons, testSquare, isMapReady, wireframeMapviewerRef.current]);

  // Auto-zoom to test square when it's available
  useEffect(() => {
    if (!wireframeMapviewerRef.current || !isMapReady || !testSquare || hasZoomedToTestSquare) return;
    // Use the test square's centroid coordinates directly, like Adjuster.js does
    const centroidCoordinates = get(testSquare, 'centroid.coordinates', []);

    if (centroidCoordinates && centroidCoordinates.length >= 2) {
      const [lon, lat] = centroidCoordinates;

      // Use setTimeout to ensure map is fully ready
      setTimeout(() => {
        if (wireframeMapviewerRef.current && isMountedRef.current) {
          wireframeMapviewerRef.current.easeTo({
            center: [lon, lat],
            zoom: TESTSQUARE_ZOOM_LEVEL_ON_SELECTION,
            duration: 1500,
          });
          setHasZoomedToTestSquare(true);
        }
      }, 1000);
    }
  }, [testSquare, isMapReady, hasZoomedToTestSquare]);

  const handleMapInteraction = (e) => {
    const eventType = get(e, 'event.type', false);
    if (eventType === 'hover' || eventType === 'click') {
      const annotation = get(e, 'annotation.annotation', {});
      const anomalyId = get(annotation, 'properties.anomalyId', '');
      const testSquareAnomalies = get(testSquare, 'anomalies', []);

      // Get the actual anomaly data that's being rendered
      const validAnomalyIds = [];
      if (anomalies && anomalies[0] && anomalies[0].data && testSquareAnomalies.length > 0) {
        const matchingAnomalies = anomalies[0].data.filter((anomaly) => testSquareAnomalies.includes(anomaly.properties.anomalyId));
        validAnomalyIds.push(...matchingAnomalies.map((anomaly) => anomaly.properties.anomalyId));
      }

      // Only log if we actually have an annotation with an anomalyId that exists in the valid anomalies
      if (!isEmpty(annotation) && anomalyId && validAnomalyIds.includes(anomalyId)) {
        onAnomalyHover({ anomalyId, eventType });
      }
    }
  };

  const updateInteractionObject = (interaction, mapReadyParam = false) => {
    try {
      if (isEmpty(interaction) || typeof (interaction) !== 'object' || !get(interaction, 'type', null)) {
        throw new Error('Interaction object is not set properly');
      }
      let newInteractionObj = {
        enable: mapReadyParam || isMapReady,
      };
      if (interaction.enable === false) {
        newInteractionObj = {
          ...interactionObject,
          enable: false,
        };
      } else if (interaction.type === 'tracking') {
        newInteractionObj = {
          ...newInteractionObj,
          type: 'tracking',
          mode: { hover: true, click: true },
          handler: handleMapInteraction,
        };
      } else {
        newInteractionObj = {
          ...interaction,
          ...newInteractionObj,
        };
      }
      if (isMountedRef.current) {
        setInteractionObject(newInteractionObj);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error in updateInteractionObject:', err);
    }
  };

  const handleMapReady = () => {
    if (isMountedRef.current) {
      setIsMapReady(true);
      updateInteractionObject(
        { type: 'tracking' },
        true,
      );
    }
  };

  const handleBoundsSet = ({ view }) => {
    if (isMountedRef.current) {
      if (!initialZoomLevel) {
        setInitialZoomLevel(view.zoom);
      }
      setCurrentView(view);
    }
  };

  const onMapViewUpdated = ({ bound, ...newView }) => {
    if (isMountedRef.current) {
      setLocalCurrentView({ ...newView });
    }
  };

  const zoomWithEaseTo = (zoom) => {
    // Clamp zoom to be within [MAPVIEWER_MIN_ZOOM, MAPVIEWER_MAX_ZOOM], inclusive
    const newZoom = Math.min(Math.max(zoom, MAPVIEWER_MIN_ZOOM), MAPVIEWER_MAX_ZOOM);
    wireframeMapviewerRef.current.easeTo({
      zoom: newZoom,
      duration: 1200,
      essential: true,
      animate: true,
    });
  };

  const zoomInClick = () => {
    const newZoomLevel = localCurrentView.zoom + 1;
    switch (true) {
      case newZoomLevel <= MAPVIEWER_MAX_ZOOM:
        zoomWithEaseTo(localCurrentView.zoom + 1);
        break;
      case newZoomLevel > MAPVIEWER_MAX_ZOOM: {
        if (localCurrentView.zoom !== MAPVIEWER_MAX_ZOOM) {
          zoomWithEaseTo(MAPVIEWER_MAX_ZOOM);
          break;
        }
        break;
      }
      default:
    }
  };

  const zoomOutClick = () => {
    const newZoomLevel = localCurrentView.zoom - 1;
    if (parseFloat(newZoomLevel.toFixed(4)) <= parseFloat(initialZoomLevel || 0)) {
      zoomWithEaseTo(Math.max(parseFloat(initialZoomLevel), MAPVIEWER_MIN_ZOOM));
    } else {
      zoomWithEaseTo(localCurrentView.zoom - 1);
    }
  };

  // --- Asset Management (following PhotosMapViewer pattern) ---

  // Background assets are static - use useMemo to calculate once
  const backgroundAsset = useMemo(() => {
    const backgroundData = [];
    if (facetPolygons && facetPolygons.data) {
      facetPolygons.data.forEach((facet) => {
        backgroundData.push({
          ...facet,
          properties: {
            ...facet.properties,
            trackEnabled: false,
          },
          style: {
            strokeColor: 'black',
            strokeWidth: 1,
            strokeOpacity: 1,
            fillOpacity: 0,
          },
        });
      });
    }
    if (testSquare && testSquare.geometry) {
      backgroundData.push({
        id: `test-square-${testSquare.testSquareId}`,
        geometries: [testSquare.geometry],
        properties: {
          annotationType: 'box',
          assetType: 'testSquare',
          testSquareId: testSquare.testSquareId,
          trackEnabled: false,
        },
        style: {
          strokeColor: 'blue',
          strokeWidth: 2,
          strokeOpacity: 1,
          fillOpacity: 0,
          cursorStyle: '',
        },
      });
    }

    if (backgroundData.length > 0) {
      return util.getMapviewerAsset({
        ...facetPolygons,
        data: backgroundData,
      });
    }
    return null;
  }, [facetPolygons, testSquare]);

  // Function to get anomaly background image based on status, hover, and selection state
  // Based on Adjuster.js getAnomalyStyleBackground function
  const getAnomalyStyleBackground = (anomaly, isHovered = false) => {
    let style = opportunityImg;
    try {
      const status = get(anomaly, 'properties.status', '');
      const userAddedAnomaly = get(anomaly, 'properties.isUserAdded', false);

      // Priority 1: Hovered anomaly always uses selected image
      if (isHovered) {
        return userAddedAnomaly ? userAnomalySelectedImg : selectedImg;
      }

      // Priority 3: Status-based logic (following Adjuster.js pattern)
      if (userAddedAnomaly) {
        style = userAnomalyImg;
      } else {
        switch (status) {
          case 'CONFIRMED':
            style = validatedImg;
            break;
          case 'HIDDEN':
            style = hiddenImg;
            break;
          case 'REMOVED':
            style = hiddenImg;
            break;
          case 'UNCONFIRMED':
            style = opportunityImg;
            break;
          default:
            style = opportunityImg;
            break;
        }
      }
      return style;
    } catch (err) {
      return style;
    }
  };

  // Function to create anomaly asset (dynamic for highlighting)
  const createAnomalyAsset = () => {
    const testSquareAnomalies = get(testSquare, 'anomalies', []);
    if (anomalies && anomalies[0] && anomalies[0].data && testSquareAnomalies.length > 0) {
      const matchingAnomalies = anomalies[0].data.filter((anomaly) => testSquareAnomalies.includes(anomaly.properties.anomalyId));
      if (matchingAnomalies.length > 0) {
        const anomalyAssetOptions = {
          ...anomalies[0],
          data: matchingAnomalies.map((anomaly) => {
            const { anomalyId } = anomaly.properties;
            const isHovered = anomalyId === hoveredAnomalyId;

            // Get the appropriate background image using our improved function
            const backgroundImage = getAnomalyStyleBackground(anomaly, isHovered);

            return {
              ...anomaly,
              properties: {
                ...anomaly.properties,
                annotationID: anomaly.properties.anomalyId,
                trackEnabled: true,
                cursorStyle: 'pointer',
              },
              style: {
                ...anomaly.style,
                strokeOpacity: 0,
                text: {
                  ...get(anomaly, 'style.text', {}),
                  backgroundImage,
                },
                point: {
                  ...(get(anomaly, 'style.point', {})),
                  fillOpacity: 0.1,
                  fillColor: '#0000FF',
                },
              },
            };
          }),
        };
        return util.getMapviewerAsset(anomalyAssetOptions);
      }
    }
    return null;
  };

  // Function to update anomaly visual representation based on hover/selection state
  const updateAnomalyColors = () => {
    const currentAsset = anomalyAssetRef.current;
    if (!currentAsset || !currentAsset.data) return;

    const updatedAnomalyAsset = {
      ...currentAsset,
      data: currentAsset.data.map((anomaly) => {
        const anomalyId = get(anomaly, 'properties.annotationID', '');
        const isHovered = anomalyId === hoveredAnomalyId;

        // Get the appropriate background image using our improved function
        const backgroundImage = getAnomalyStyleBackground(anomaly, isHovered);

        return {
          ...anomaly,
          style: {
            ...anomaly.style,
            text: {
              ...get(anomaly, 'style.text', {}),
              backgroundImage,
            },
          },
        };
      }),
    };
    setAnomalyAsset(updatedAnomalyAsset);
  };

  // Effect to create anomaly asset when dependencies change
  useEffect(() => {
    const asset = createAnomalyAsset();
    setAnomalyAsset(asset);
    anomalyAssetRef.current = asset; // Store in ref for highlighting
  }, [anomalies, testSquare]);

  // Effect to update anomaly colors when highlighting or hovering changes
  useEffect(() => {
    updateAnomalyColors();
  }, [hoveredAnomalyId]); // Update when either changes

  // Combine assets for rendering
  const assets = [];
  if (backgroundAsset) {
    assets.push(backgroundAsset);
  }
  if (anomalyAsset) {
    assets.push(anomalyAsset);
  }
  // --- End Asset Management ---

  const zoomInDisabled = localCurrentView.zoom >= MAPVIEWER_MAX_ZOOM;
  const zoomOutDisabled = localCurrentView.zoom <= Math.max(MAPVIEWER_MIN_ZOOM, parseFloat(initialZoomLevel || 0));

  // Function to get pixel coordinates for a specific anomaly
  const getAnomalyPixelPosition = (anomalyId) => {
    if (!wireframeMapviewerRef.current || !anomalies || !anomalies[0] || !anomalies[0].data) {
      return null;
    }

    // Find the anomaly with the given ID
    const anomaly = anomalies[0].data.find((a) => a.properties.anomalyId === anomalyId);
    if (!anomaly || !anomaly.geometries || !anomaly.geometries[0]) {
      return null;
    }

    try {
      // Apply proper coordinate transformation using the anomaly's asset
      const translatedCoordinates = util.getTopLeftCoordinate(anomaly.geometries[0].coordinates[0], 0);

      if (translatedCoordinates.length !== 3) {
        return null;
      }

      // Convert to pixel coordinates using the mapviewer's project function
      const pixelPosition = wireframeMapviewerRef.current.project(translatedCoordinates);
      const { x, y } = pixelPosition;

      return {
        x,
        y,
        anomalyId,
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error getting anomaly pixel position:', error);
      return null;
    }
  };

  // Expose functions through ref
  useImperativeHandle(ref, () => ({
    getAnomalyPixelPosition,
    easeTo: (options) => {
      if (wireframeMapviewerRef.current && wireframeMapviewerRef.current.easeTo) {
        return wireframeMapviewerRef.current.easeTo(options);
      }
      // eslint-disable-next-line no-console
      console.warn('MapViewer not ready or easeTo method not available');
      return null;
    },
  }));

  // Cleanup effect to prevent memory leaks and state updates on unmounted component
  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  return (
    <EVBox className={styles.mapViewerOuterContainer}>
      <EVBox className={styles.mapViewerContainer}>
        <EVMapViewer
          ref={wireframeMapviewerRef}
          view={currentView}
          baseMap="blank"
          fitToBounds
          enableFlyTo
          flyToControl={{
            speed: 0.5,
          }}
          bound={bounds}
          initialConfig={{
            style: { backgroundColor: '#fff' },
            mapBoxKey: MAPBOX_KEY,
          }}
          navigationControl={{
            disablePitchAndRotation: true,
          }}
          maxZoom={MAPVIEWER_MAX_ZOOM}
          assets={isMapReady ? assets : []}
          onMapReady={handleMapReady}
          onBoundsSet={handleBoundsSet}
          onViewUpdated={onMapViewUpdated}
          interaction={interactionObject}
        />
      </EVBox>
      <EVBox className={styles.compassContainer}>
        <img className={styles.compassIcon} src={Compass} alt="Compass" />
        <EVToolbarPanel
          displayType="vertical"
          iconStyles={{ height: 'auto', borderRadius: '4px' }}
          backgroundColor={theme.palette.common.white}
          style={{ borderRadius: '4px' }}
          border="1px solid #ccc"
        >
          <EVIconButton
            data-testid="zoomInButton"
            key="1"
            id="zoomInButton"
            disabled={zoomInDisabled}
            onClick={zoomInClick}
          >
            <EVAdd />
          </EVIconButton>
          <EVIconButton
            data-testid="zoomOutButton"
            key="2"
            id="zoomOutButton"
            disabled={zoomOutDisabled}
            onClick={zoomOutClick}
          >
            <EVRemove />
          </EVIconButton>
        </EVToolbarPanel>
      </EVBox>
    </EVBox>
  );
});

TestSquareWireFrameMapViewer.propTypes = {
  facetPolygons: PropTypes.shape({
    uuid: PropTypes.string,
    assetType: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      geometries: PropTypes.arrayOf(PropTypes.number),
      properties: PropTypes.shape({}),
      style: PropTypes.shape({}),
      georeference: PropTypes.string,
    })),
  }),
  anomalies: PropTypes.arrayOf(PropTypes.shape({
    uuid: PropTypes.string,
    assetType: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      geometries: PropTypes.arrayOf(PropTypes.number),
      properties: PropTypes.shape({}),
      style: PropTypes.shape({}),
      georeference: PropTypes.string,
    })),
  })),
  onAnomalyHover: PropTypes.func,
  hoveredAnomalyId: PropTypes.string,
};

TestSquareWireFrameMapViewer.defaultProps = {
  facetPolygons: null,
  anomalies: [],
  onAnomalyHover: () => {},
  hoveredAnomalyId: null,
};

export default TestSquareWireFrameMapViewer;
